import React, { useEffect, useState, useCallback } from "react";
import { Search, Plus, Trash2, Edit3, X } from "lucide-react";
import { toast } from "sonner";
import { api, fmtCAD } from "@/lib/api";

const empty = { title: "", brand: "Frigidaire", category_key: "", category_label: "", subtype: "", price: 0, compare_at: null, sku: "", in_stock: true, image: "", images: [], description: "", featured: false };

export default function AdminProducts() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // product or 'new'
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/products", { params: { search } }).then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, [search]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openNew = () => { setForm(empty); setEditing("new"); };
  const openEdit = (p) => { setForm({ ...p, images: p.images || [] }); setEditing(p); };
  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price: parseFloat(form.price) || 0,
      compare_at: form.compare_at ? parseFloat(form.compare_at) : null,
      images: form.image ? [form.image] : [],
    };
    try {
      if (editing === "new") await api.post("/admin/products", payload);
      else await api.put(`/admin/products/${editing.id}`, payload);
      toast.success("Produit enregistré");
      setEditing(null); load();
    } catch (err) { toast.error("Erreur", { description: err.response?.data?.detail || err.message }); }
  };

  const del = async (p) => {
    if (!window.confirm(`Supprimer « ${p.title} » ?`)) return;
    await api.delete(`/admin/products/${p.id}`); toast.success("Supprimé"); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-3xl font-extrabold">Produits</h1>
        <button onClick={openNew} className="flex items-center gap-2 bg-sapphire text-white px-5 py-2.5 font-semibold" data-testid="admin-new-product"><Plus size={18} /> Nouveau produit</button>
      </div>

      <div className="flex items-center border border-gray-300 bg-white px-3 mb-5 max-w-md">
        <Search size={16} className="text-gray-400" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher (nom, réf)…" className="px-2 py-2.5 outline-none w-full" data-testid="admin-product-search" />
      </div>

      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500"><tr>
            <th className="p-4">Produit</th><th className="p-4">Marque</th><th className="p-4">Réf</th><th className="p-4">Prix</th><th className="p-4">Stock</th><th className="p-4"></th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="p-8 text-center text-gray-400">Chargement…</td></tr> :
            rows.map((p) => (
              <tr key={p.id} className="border-t border-gray-100" data-testid={`admin-product-row-${p.id}`}>
                <td className="p-4 flex items-center gap-3">
                  <div className="h-10 w-10 bg-gray-50 border border-gray-200 flex items-center justify-center shrink-0">{p.image && <img src={p.image} alt="" className="max-h-full max-w-full object-contain p-1" />}</div>
                  <span className="font-medium line-clamp-1 max-w-xs">{p.title}</span>
                </td>
                <td className="p-4">{p.brand}</td>
                <td className="p-4 text-gray-500">{p.sku || "—"}</td>
                <td className="p-4 font-semibold">{fmtCAD(p.price)}</td>
                <td className="p-4">{p.in_stock ? <span className="text-green-600">Oui</span> : <span className="text-gray-400">Non</span>}</td>
                <td className="p-4">
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => openEdit(p)} className="p-2 text-gray-500 hover:text-sapphire" data-testid={`admin-edit-${p.id}`}><Edit3 size={16} /></button>
                    <button onClick={() => del(p)} className="p-2 text-gray-500 hover:text-red-500" data-testid={`admin-delete-${p.id}`}><Trash2 size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-gray-400">Aucun produit.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white w-full max-w-2xl max-h-[90vh] overflow-auto p-8" data-testid="admin-product-form">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold">{editing === "new" ? "Nouveau produit" : "Modifier le produit"}</h2>
              <button type="button" onClick={() => setEditing(null)}><X /></button>
            </div>
            <div className="space-y-4">
              <div><label className="block text-sm font-medium mb-1">Titre *</label><input value={form.title} onChange={set("title")} required className="w-full border border-gray-300 px-4 py-2.5" data-testid="form-title" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium mb-1">Marque</label><input value={form.brand} onChange={set("brand")} className="w-full border border-gray-300 px-4 py-2.5" data-testid="form-brand" /></div>
                <div><label className="block text-sm font-medium mb-1">Référence (SKU)</label><input value={form.sku} onChange={set("sku")} className="w-full border border-gray-300 px-4 py-2.5" data-testid="form-sku" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium mb-1">Clé catégorie</label><input value={form.category_key} onChange={set("category_key")} placeholder="refrigerators" className="w-full border border-gray-300 px-4 py-2.5" data-testid="form-cat-key" /></div>
                <div><label className="block text-sm font-medium mb-1">Catégorie (affichée)</label><input value={form.category_label} onChange={set("category_label")} placeholder="Réfrigérateurs" className="w-full border border-gray-300 px-4 py-2.5" data-testid="form-cat-label" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium mb-1">Prix (CAD) *</label><input type="number" step="0.01" value={form.price} onChange={set("price")} required className="w-full border border-gray-300 px-4 py-2.5" data-testid="form-price" /></div>
                <div><label className="block text-sm font-medium mb-1">Prix barré</label><input type="number" step="0.01" value={form.compare_at || ""} onChange={set("compare_at")} className="w-full border border-gray-300 px-4 py-2.5" data-testid="form-compare" /></div>
              </div>
              <div><label className="block text-sm font-medium mb-1">Image (URL)</label><input value={form.image} onChange={set("image")} className="w-full border border-gray-300 px-4 py-2.5" data-testid="form-image" /></div>
              <div><label className="block text-sm font-medium mb-1">Description</label><textarea value={form.description} onChange={set("description")} rows={3} className="w-full border border-gray-300 px-4 py-2.5" data-testid="form-description" /></div>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.in_stock} onChange={set("in_stock")} data-testid="form-instock" /> En stock</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={set("featured")} data-testid="form-featured" /> En vedette</label>
              </div>
            </div>
            <div className="flex gap-3 mt-8">
              <button type="submit" className="flex-1 bg-sapphire text-white font-semibold py-3" data-testid="form-save">Enregistrer</button>
              <button type="button" onClick={() => setEditing(null)} className="px-6 border border-gray-300 font-semibold">Annuler</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
